import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import CouponInput from '../AdminDashboard/CouponInput';
import {
    FaCalculator,
    FaBrain,
    FaPenFancy,
    FaBook,
    FaCheckCircle,
    FaStar,
    FaArrowRight,
    FaLock,
    FaArrowLeft,
    FaHistory,
    FaChevronRight
} from "react-icons/fa";
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const IndividualMockTests = () => {
    const [firstname, setFirstName] = useState("");
    const [loading, setLoading] = useState(true);
    const [studentId, setStudentId] = useState(null);
    const [subjectAccess, setSubjectAccess] = useState({});
    const [appliedCoupons, setAppliedCoupons] = useState({});
    const [testHistory, setTestHistory] = useState([]);
    const navigate = useNavigate();

    const subjects = [
        {
            key: 'Maths',
            name: 'Mathematical Reasoning Mock Test',
            icon: <FaCalculator className="text-xl" />,
            color: "from-blue-500 to-teal-400",
            bgColor: "bg-blue-50",
            textColor: "text-blue-700",
            price: "$95",
            duration: "40 minutes",
            isAvailable: true // Maths is available
        },
        {
            key: 'ThinkingSkills',
            name: 'Thinking Skills Mock Test',
            icon: <FaBrain className="text-xl" />,
            color: "from-purple-500 to-pink-400",
            bgColor: "bg-purple-50",
            textColor: "text-purple-700",
            price: "$95",
            duration: "40 minutes",
            isAvailable: false // Coming Soon
        },
        {
            key: 'Writing',
            name: 'Writing Mock Test',
            icon: <FaPenFancy className="text-xl" />,
            color: "from-orange-500 to-yellow-400",
            bgColor: "bg-orange-50",
            textColor: "text-orange-700",
            price: "$95",
            duration: "30 minutes",
            isAvailable: false // Coming Soon
        },
        {
            key: 'Reading',
            name: 'Reading Mock Test',
            icon: <FaBook className="text-xl" />,
            color: "from-teal-500 to-green-400",
            bgColor: "bg-teal-50",
            textColor: "text-teal-700",
            price: "$95",
            duration: "45 minutes",
            isAvailable: false // Coming Soon
        }
    ];

    useEffect(() => {
        const fetchData = async () => {
            const userData = localStorage.getItem("user");
            if (userData) {
                const user = JSON.parse(userData);
                setStudentId(user.id);
                setFirstName(user.firstName || user.username.split(" ")[0]);

                try {
                    // ✅ Fetch test history for INDIVIDUAL tests
                    const historyResponse = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/student/test-history/${user.id}`
                    );
                    
                    if (historyResponse.ok) {
                        const historyData = await historyResponse.json();
                        // Filter ONLY individual mock tests
                        const individualMockHistory = historyData.filter(test => 
                            test.testType === 'mock' && 
                            test.test_category === 'individual-mock'
                        );
                        setTestHistory(individualMockHistory);
                        console.log("Individual mock test history:", individualMockHistory);
                    }

                    // Check individual mock test access for each subject
                    const accessPromises = subjects.map(async (subject) => {
                        const response = await fetch(
                            `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/check-individual-access/${user.id}/${subject.key}`
                        );
                        const data = await response.json();
                        return { [subject.key]: data.hasAccess };
                    });

                    const accessResults = await Promise.all(accessPromises);
                    const accessMap = accessResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
                    setSubjectAccess(accessMap);
                    
                } catch (error) {
                    console.error("Error fetching data:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, []);

    // ✅ FIX: Update getTestDisplayName to properly show individual test names
    const getTestDisplayName = (test) => {
        if (test.testType === 'mock') {
            // If it's an individual mock test
            if (test.test_category === 'individual-mock' || 
                (test.testId && typeof test.testId === 'string' && test.testId.includes('individual-'))) {
                return getSubjectDisplayName(test.subject) + ' (Individual)';
            }
            
            // If testName exists, use it
            if (test.testName) {
                return test.testName;
            }

            // For package tests with testId
            if (test.testId && typeof test.testId === 'string') {
                const testMatch = test.testId.match(/mock-package-(\d+)/);
                if (testMatch) {
                    const testNumber = testMatch[1];
                    const subjectDisplayName = getSubjectDisplayName(test.subject || '');
                    return `Mock Test ${testNumber} - ${subjectDisplayName}`;
                }
            }

            // For regular mock tests - just return the subject display name
            if (test.subject) {
                return getSubjectDisplayName(test.subject);
            }

            return 'Mock Test';
        }

        // For non-mock tests
        return `${getSubjectDisplayName(test.subject || test.category || 'Unknown')} ${test.testType || 'Test'}`;
    };

    // Rest of your functions (getScoreDisplay, getScoreText, formatTestDate, getSubjectDisplayName) remain the same
    const getScoreDisplay = (test) => {
        // For writing tests, show the actual score (0-10) instead of percentage
        if (test.subject === 'writing') {
            const hasTeacherScore = test.teacher_score !== null && test.teacher_score !== undefined;
            const writingScore = hasTeacherScore ? test.teacher_score : (test.score || 0);

            return {
                displayValue: `${writingScore}/10`,
                percentage: writingScore * 10,
                isWriting: true,
                rawScore: writingScore,
                hasTeacherScore: hasTeacherScore
            };
        } else {
            return {
                displayValue: `${Math.round(test.scorePercentage || 0)}%`,
                percentage: test.scorePercentage || 0,
                isWriting: false,
                rawScore: test.correctAnswers || 0
            };
        }
    };

    const getScoreText = (test) => {
        if (test.subject === 'writing') {
            const scoreData = getScoreDisplay(test);
            if (scoreData.rawScore >= 8) return 'Excellent';
            if (scoreData.rawScore >= 6) return 'Good';
            if (scoreData.rawScore >= 4) return 'Average';
            return 'Needs Improvement';
        } else {
            const percentage = test.scorePercentage || 0;
            if (percentage >= 70) return 'Excellent';
            if (percentage >= 40) return 'Good';
            return 'Needs Improvement';
        }
    };

    const formatTestDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getSubjectDisplayName = (subjectKey) => {
        let normalizedKey = subjectKey;
        if (typeof normalizedKey === 'string') {
            normalizedKey = normalizedKey.trim();
            if (normalizedKey === 'maths') normalizedKey = 'Maths';
            if (normalizedKey === 'thinkingskills') normalizedKey = 'ThinkingSkills';
            if (normalizedKey === 'writing') normalizedKey = 'Writing';
            if (normalizedKey === 'reading') normalizedKey = 'Reading';
        }

        const displayNames = {
            'Maths': 'Mathematical Reasoning Mock Test',
            'ThinkingSkills': 'Thinking Skills Mock Test',
            'Reading': 'Reading Mock Test',
            'Writing': 'Writing Mock Test'
        };
        return displayNames[normalizedKey] || subjectKey;
    };

    const handleBuyNow = async (subject, e) => {
        e.preventDefault();
        const stripe = await stripePromise;
        const userData = localStorage.getItem("user");
        const user = userData ? JSON.parse(userData) : null;

        const endpoint = appliedCoupons[subject.key]
            ? "/api/payment/create-individual-mocktest-session-with-coupon"
            : "/api/payment/create-individual-mocktest-session";

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                student_id: studentId,
                subject: subject.key,
                coupon_code: appliedCoupons[subject.key]?.id
            }),
        });

        if (response.ok) {
            const session = await response.json();
            const result = await stripe.redirectToCheckout({ sessionId: session.id });
            if (result.error) alert(result.error.message);
        } else {
            alert("Error creating payment session");
        }
    };

    const handleStartTest = (subject) => {
        navigate('/individual-mock-test-start', {
            state: {
                subject: subject.key,
                testType: 'individual',
                testName: subject.name,
                individualTest: true
            }
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-purple-100 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/mock-test')}
                    className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
                >
                    <FaArrowLeft className="mr-2" />
                    Back to Mock Test Center
                </button>

                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        Individual Mock Tests
                    </h1>
                    <p className="text-lg text-gray-600">Purchase and take individual subject mock tests</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {subjects.map((subject) => (
                        <div
                            key={subject.key}
                            className={`group relative p-[2px] rounded-xl bg-gradient-to-br ${subject.color} hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl`}
                        >
                            {/* Coming Soon Badge - Only show if not available and not Maths */}
                            {!subject.isAvailable && subject.key !== 'Maths' && (
                                <div className="absolute top-1 -right-10 z-10">
                                    <div className="bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg transform rotate-[30deg] border-2 border-white">
                                        COMING SOON
                                    </div>
                                </div>
                            )}
                            
                            <div className="bg-white rounded-xl overflow-hidden h-full relative">
                                <div className="p-6 h-full flex flex-col">
                                    <div className="flex flex-col items-center text-center mb-4">
                                        <div className={`${subject.bgColor} p-4 rounded-full group-hover:opacity-90 transition mb-4`}>
                                            <div className={`${subject.textColor}`}>
                                                {subject.icon}
                                            </div>
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-800 group-hover:text-gray-900 transition">
                                            {subject.name}
                                        </h2>
                                        <span className={`${subject.bgColor} ${subject.textColor} px-3 py-1 rounded-full text-sm font-medium mt-2`}>
                                            {subject.price}/test
                                        </span>
                                        <p className="text-gray-500 text-sm mt-1">{subject.duration}</p>
                                    </div>

                                    <div className="space-y-2 mb-4 flex-grow">
                                        <div className="flex items-start">
                                            <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                            <span className="text-gray-700 text-sm">Full-length mock test</span>
                                        </div>
                                        <div className="flex items-start">
                                            <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                            <span className="text-gray-700 text-sm">Real exam conditions</span>
                                        </div>
                                        <div className="flex items-start">
                                            <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                            <span className="text-gray-700 text-sm">Detailed performance report</span>
                                        </div>
                                    </div>

                                    {/* Coupon Input Section */}
                                    {!subjectAccess[subject.key] && subject.isAvailable && (
                                        <div className="mt-2">
                                            <CouponInput
                                                onCouponApplied={(coupon) => setAppliedCoupons(prev => ({ ...prev, [subject.key]: coupon }))}
                                                onCouponRemoved={() => setAppliedCoupons(prev => {
                                                    const newCoupons = { ...prev };
                                                    delete newCoupons[subject.key];
                                                    return newCoupons;
                                                })}
                                                type={`mocktest-${subject.key.toLowerCase()}`}
                                            />
                                        </div>
                                    )}

                                    <div className="mt-4">
                                        {subjectAccess[subject.key] && subject.isAvailable ? (
                                            <button
                                                onClick={() => handleStartTest(subject)}
                                                className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-medium rounded-lg hover:opacity-90 transition hover:shadow-md"
                                            >
                                                Start Test
                                            </button>
                                        ) : subject.isAvailable ? (
                                            <button
                                                onClick={(e) => handleBuyNow(subject, e)}
                                                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:opacity-90 transition hover:shadow-md flex items-center justify-center space-x-2"
                                            >
                                                <FaLock className="text-sm" />
                                                <span>Buy Now - {subject.price}</span>
                                            </button>
                                        ) : (
                                            <button
                                                disabled
                                                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg opacity-70 cursor-not-allowed flex items-center justify-center space-x-2"
                                            >
                                                <FaLock className="text-sm" />
                                                <span>Coming Soon</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ✅ Test History Section - Now properly populated */}
                <div className="bg-white rounded-xl shadow-md p-6 mt-8">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-2xl font-bold text-gray-800 flex items-center">
                            <FaHistory className="mr-2 text-blue-500" /> Your Individual Mock Test History
                        </h3>
                        <button
                            onClick={() => navigate('/test-history', { state: { filter: 'individual-mock' } })}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            View All <FaChevronRight className="inline ml-1" />
                        </button>
                    </div>

                    {testHistory.length > 0 ? (
                        <div className="space-y-4">
                            {testHistory.slice(0, 3).map((test, index) => {
                                const scoreData = getScoreDisplay(test);
                                const scoreText = getScoreText(test);

                                return (
                                    <div
                                        key={index}
                                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => navigate(`/test-results/mock/${test.testId}`)}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div className="flex-1">
                                                <h4 className="font-bold text-gray-800">
                                                    {getTestDisplayName(test)}
                                                </h4>
                                                <p className="text-sm text-gray-500">{formatTestDate(test.completedAt)}</p>

                                                <div className="text-sm text-gray-600 mt-1">
                                                    {test.subject === 'writing' ? (
                                                        <div className="space-y-1">
                                                            <div>
                                                                <span className="font-medium">
                                                                    Score: {scoreData.displayValue}
                                                                    {scoreData.hasTeacherScore && (
                                                                        <span className="ml-1 text-xs text-green-600">(Teacher Graded)</span>
                                                                    )}
                                                                </span>
                                                                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                                                    scoreData.rawScore >= 8 ? 'bg-green-100 text-green-800' :
                                                                    scoreData.rawScore >= 6 ? 'bg-blue-100 text-blue-800' :
                                                                    scoreData.rawScore >= 4 ? 'bg-yellow-100 text-yellow-800' :
                                                                    'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {scoreText}
                                                                </span>
                                                            </div>
                                                            {test.teacher_feedback && (
                                                                <div className="text-xs text-gray-500">
                                                                    Teacher feedback available
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p>
                                                            {/* Score info for MCQ tests */}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Show teacher grading status for writing tests */}
                                                {test.subject === 'writing' && test.status === 'pending_admin_review' && (
                                                    <div className="mt-1">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                                            Awaiting Teacher Review
                                                        </span>
                                                    </div>
                                                )}
                                                {test.subject === 'writing' && test.status === 'assigned' && (
                                                    <div className="mt-1">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                                            Assigned to Teacher
                                                        </span>
                                                    </div>
                                                )}
                                                {test.subject === 'writing' && test.status === 'graded' && scoreData.hasTeacherScore && (
                                                    <div className="mt-1">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                                            ✅ Teacher Graded
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex items-center ml-4">
                                                <div style={{ width: 50, height: 50, marginRight: 12 }}>
                                                    <CircularProgressbar
                                                        value={scoreData.percentage}
                                                        text={scoreData.isWriting ? `${scoreData.rawScore}` : `${Math.round(scoreData.percentage)}%`}
                                                        styles={buildStyles({
                                                            textSize: scoreData.isWriting ? '32px' : '28px',
                                                            pathColor: scoreData.percentage >= 70 ? '#10B981' :
                                                                scoreData.percentage >= 40 ? '#F59E0B' : '#EF4444',
                                                            textColor: scoreData.percentage >= 70 ? '#10B981' :
                                                                scoreData.percentage >= 40 ? '#F59E0B' : '#EF4444',
                                                            trailColor: '#E5E7EB',
                                                        })}
                                                    />
                                                </div>
                                                {test.subject === 'writing' && (
                                                    <div className="text-xs text-gray-500 text-center">
                                                        /10
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="inline-block p-4 rounded-full bg-gray-100 mb-4">
                                <FaHistory className="text-3xl text-gray-400" />
                            </div>
                            <p className="text-gray-500">No individual mock test history available</p>
                            <p className="text-gray-400 text-sm mt-2">Complete your first individual mock test to see your history here</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IndividualMockTests;